import { FC } from 'react';
import { Modal, ModalProps } from 'src/common/components/Modal';
import { Button } from 'src/common/components/Button';
import { media } from 'src/common/styles/breakpoints';
import styled, { css } from 'styled-components';
import {
  RetryChoice,
  RetryMessageDTO,
} from 'src/pages/VerificationPage/verificationModels';
import ReactMarkdown from 'react-markdown';
import { useHistory } from 'react-router';
import { MainPageRoute, verificationIdParam } from 'src/routes';
import { ReactComponent as ExclamationIcon } from 'src/assets/icons/exclamation.svg';
import { useProcessRetryMutation } from 'src/pages/VerificationPage/verificationQueries';
import { useQueryClient } from '@tanstack/react-query';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';

const StyledModal = styled(Modal)`
  ${media.w.min.px576(css`
    > :first-child {
      width: 35rem;
    }
  `)}
`;

const Body = styled.div`
  margin: 2rem 0;
  min-height: 25vh;
`;

const Title = styled.h4`
  font-size: 1rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text.primary};
  text-align: justify;

  ${media.w.min.px576(css`
    font-size: 1.125rem;
  `)}
`;

const Description = styled.div`
  margin: 1.5rem 0;
  color: ${({ theme }) => theme.text.secondary};
  text-align: justify;
  font-size: 0.8125rem;

  ${media.w.min.px576(css`
    font-size: 1rem;
  `)}
`;

const Icon = styled(ExclamationIcon)`
  flex-shrink: 0;
  margin-right: 1rem;
  width: 2rem;
  height: 2rem;
  color: ${({ theme }) => theme.colors.primary};

  ${media.w.min.px576(css`
    width: 2.5rem;
    height: 2.5rem;
  `)}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CancelButton = styled(Button)`
  font-size: ${({ theme }) =>
    `calc(${theme.button.size.default.default?.fontSize} - 0.25rem)`};

  ${media.w.min.px576(css`
    font-size: ${({ theme }) =>
      theme.button.size.default.sm?.fontSize &&
      `calc(${theme.button.size.default.sm.fontSize} - 0.25rem)`};
  `)}
`;

const StyledReactMarkdown = styled(ReactMarkdown)`
  white-space: pre-wrap;
`;

interface RetryModalProps extends ModalProps {
  verificationId: string;
  message: RetryMessageDTO;
}

export const RetryModal: FC<RetryModalProps> = ({
  verificationId,
  message,
  onClose,
  ...props
}) => {
  const history = useHistory();
  const { mutate, isLoading } = useProcessRetryMutation();
  const queryClient = useQueryClient();

  const onAccept = () => {
    mutate(
      { verificationId, choice: RetryChoice.Retry },
      {
        onSuccess: (res) => {
          localStorage.setItem(verificationIdParam, res.verificationId);
          history.replace({
            pathname: MainPageRoute,
            search: `?${verificationIdParam}=${res.verificationId}`,
          });
          queryClient.clear();
        },
      }
    );
  };

  const onCancel = () => {
    mutate(
      { verificationId, choice: RetryChoice.Finish },
      {
        onSuccess: (res) => {
          queryClient.setQueryData(
            verificationQueryKeys.status(verificationId),
            res
          );
          onClose();
        },
      }
    );
  };

  return (
    <StyledModal
      disableEscapeKeyDown={true}
      disableEscapeBackdropClick={true}
      onClose={onClose}
      {...props}
    >
      <>
        <Header>
          <Icon />
          <Title>{message.header}</Title>
        </Header>
        <Body>
          <Description>
            <StyledReactMarkdown>{message.body}</StyledReactMarkdown>
          </Description>
        </Body>
      </>

      <ButtonsContainer>
        <Button onClick={onAccept} disabled={isLoading}>
          Ponowna weryfikacja
        </Button>

        <CancelButton
          $variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Rezygnuję z ponownej weryfikacji
        </CancelButton>
      </ButtonsContainer>
    </StyledModal>
  );
};
